import { Loading } from "../../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  formatEuro,
  quoteDisplayStatus,
  SALES_CUSTOMER_TYPES,
  SALES_LOG_INPUT_KINDS,
  SALES_LOG_KIND_LABELS,
  SALES_STAGE_LABELS,
  SALES_STAGES,
  salesStepLate,
  type PlatformOrgRow,
  type SalesContactDto,
  type SalesCustomerDetail,
  type SalesCustomerRow,
  type SalesLogKind,
  type SalesQuoteRow,
  type SalesStage,
} from "@klantpunt/shared";
import { IconChevronRight, IconPencil, IconPlus, IconTrash } from "../../app/icons";
import { Meta } from "../../app/Meta";
import { Modal } from "../../app/Modal";
import { PageHeader } from "../../app/PageHeader";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { PlatformGuard } from "../../pages/Platform";
import { PlatformHeader } from "./PlatformHeader";
import { NewQuoteDialog, QuoteStatusChip, quotesKey } from "./SalesQuotes";

const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");
const listKey = ["platform", "sales", "customers"] as const;
const detailKey = (id: string) => ["platform", "sales", "customer", id] as const;
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtDay = (iso: string | null) =>
  iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })
    : "";
const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
const initials = (name: string) =>
  name
    .replace(/\b(B\.?V\.?|N\.?V\.?|VvE|Stichting)\b/gi, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");

const STAGE_TONE: Record<SalesStage, string> = {
  lead: "",
  demo: "tone tone-info",
  offer: "tone tone-accent",
  client: "tone tone-ok",
  lost: "tone tone-danger",
};
export function StageChip({ stage }: { stage: SalesStage }) {
  return <span className={`chip ${STAGE_TONE[stage]}`.trim()}>{SALES_STAGE_LABELS[stage]}</span>;
}

function NextStep({ c, today }: { c: Pick<SalesCustomerRow, "nextStepOn" | "nextStepText">; today: string }) {
  if (!c.nextStepOn && !c.nextStepText) return <span className="muted">—</span>;
  const late = salesStepLate(c.nextStepOn, today);
  return (
    <>
      {c.nextStepOn && <span className={late ? "co-late" : "sales-date"}>{fmtDay(c.nextStepOn)}</span>}
      {c.nextStepText && <span className="small muted co-block">{c.nextStepText}</span>}
    </>
  );
}

// ---------------------------------------------------------------- lijst

export function SalesCustomersPage() {
  return (
    <PlatformGuard>
      <SalesCustomers />
    </PlatformGuard>
  );
}

function SalesCustomers() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: listKey, queryFn: () => api<SalesCustomerRow[]>("/platform/sales/customers") });
  const [stage, setStage] = useState<SalesStage | "late" | null>(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const today = todayIso();
  const all = useMemo(() => q.data ?? [], [q.data]);
  const late = useMemo(() => all.filter((c) => c.stage !== "lost" && salesStepLate(c.nextStepOn, today)), [all, today]);
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return all.filter((c) => {
      if (stage === "late" ? !late.includes(c) : stage && c.stage !== stage) return false;
      if (!stage && c.stage === "lost") return false;
      if (!s) return true;
      return [c.name, c.type, c.city, c.mainContact?.name, c.orgName].some((v) => v?.toLowerCase().includes(s));
    });
  }, [all, stage, search, late]);
  const count = (s: SalesStage) => all.filter((c) => c.stage === s).length;

  return (
    <div className="page platform sales-page">
      <PlatformHeader
        title="Klanten"
        action={
          <button type="button" className="btn primary" onClick={() => setCreating(true)}>
            <IconPlus size={20} aria-hidden="true" />
            Klant
          </button>
        }
      />
      <div className="dir-filters">
        <input
          type="search"
          className="dir-search"
          aria-label="Zoeken in klanten"
          placeholder="Zoeken op naam, plaats of contactpersoon"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="dir-kinds" role="group" aria-label="Fase">
          <button type="button" className="kpi" aria-pressed={stage === null} onClick={() => setStage(null)}>
            Lopend <span className="count num">{all.filter((c) => c.stage !== "lost").length}</span>
          </button>
          {SALES_STAGES.map((s) => (
            <button
              key={s}
              type="button"
              className="kpi"
              aria-pressed={stage === s}
              onClick={() => setStage(stage === s ? null : s)}
            >
              {SALES_STAGE_LABELS[s]} <span className="count num">{count(s)}</span>
            </button>
          ))}
          {late.length > 0 && (
            <button
              type="button"
              className="kpi doc-attn"
              aria-pressed={stage === "late"}
              onClick={() => setStage(stage === "late" ? null : "late")}
            >
              Volgende stap te laat <span className="count num">{late.length}</span>
            </button>
          )}
        </div>
      </div>
      <section className="panel tscroll" aria-busy={q.isPending}>
        {q.isPending ? (
          <Loading />
        ) : rows.length ? (
          <table className="it sales-table" aria-label="Klanten">
            <thead>
              <tr>
                <th scope="col">Klant</th>
                <th scope="col" className="dir-hide-narrow">
                  Fase
                </th>
                <th scope="col" className="dir-hide-narrow">
                  Contactpersoon
                </th>
                <th scope="col" className="num dir-hide-narrow">
                  Gebouwen
                </th>
                <th scope="col">Volgende stap</th>
                <th scope="col" className="dir-hide-narrow">
                  <span className="sr-only">Openen</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="co-click" onClick={() => void navigate(`/platform/klanten/${c.id}`)}>
                  <td className="tcell-title">
                    <Link to={`/platform/klanten/${c.id}`} className="co-rowtitle" onClick={(e) => e.stopPropagation()}>
                      {c.name}
                    </Link>
                    <Meta
                      className="small muted co-block"
                      items={[c.type, c.city, c.orgName && `In Klantpunt: ${c.orgName}`]}
                    />
                    <span className="sales-stage-narrow">
                      <StageChip stage={c.stage} />
                    </span>
                  </td>
                  <td className="dir-hide-narrow">
                    <StageChip stage={c.stage} />
                  </td>
                  <td className="dir-hide-narrow">
                    {c.mainContact ? (
                      <>
                        {c.mainContact.name}
                        {c.mainContact.role && <span className="small muted co-block">{c.mainContact.role}</span>}
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="num dir-hide-narrow">{c.buildings || "—"}</td>
                  <td>
                    <NextStep c={c} today={today} />
                  </td>
                  <td className="dir-hide-narrow">
                    <IconChevronRight size={18} aria-hidden="true" className="muted" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">
            {all.length
              ? "Geen klanten gevonden."
              : "Nog geen klanten. Voeg bedrijven toe die Klantpunt willen gebruiken, met hun contactpersoon en de volgende stap."}
          </p>
        )}
      </section>
      {creating && (
        <CustomerDialog
          onClose={() => setCreating(false)}
          onSaved={(c) => void navigate(`/platform/klanten/${c.id}`)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- venster klant

function CustomerDialog({
  customer: c,
  onClose,
  onSaved,
}: {
  customer?: SalesCustomerDetail;
  onClose: () => void;
  onSaved?: (c: SalesCustomerDetail) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const orgs = useQuery({ queryKey: ["platform", "orgs"], queryFn: () => api<PlatformOrgRow[]>("/platform/orgs") });
  const [f, setF] = useState({
    name: c?.name ?? "",
    type: c?.type ?? "",
    stage: c?.stage ?? ("lead" as SalesStage),
    city: c?.city ?? "",
    kvk: c?.kvk ?? "",
    website: c?.website ?? "",
    source: c?.source ?? "",
    lostReason: c?.lostReason ?? "",
    orgId: c?.orgId ?? "",
    buildings: String(c?.buildings ?? ""),
    clients: String(c?.clients ?? ""),
    notes: c?.notes ?? "",
    contactName: "",
    contactRole: "",
    contactEmail: "",
    contactPhone: "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((v) => ({ ...v, [k]: e.target.value }));
  const num = (v: string) => Math.max(0, Math.round(Number(v.replace(",", ".")) || 0));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        type: f.type,
        stage: f.stage,
        city: f.city,
        kvk: f.kvk,
        website: f.website,
        source: f.source,
        lostReason: f.stage === "lost" ? f.lostReason : "",
        orgId: f.orgId || null,
        buildings: num(f.buildings),
        clients: num(f.clients),
        notes: f.notes,
      };
      if (c) return api<SalesCustomerDetail>(`/platform/sales/customers/${c.id}`, { method: "PATCH", body });
      return api<SalesCustomerDetail>("/platform/sales/customers", {
        method: "POST",
        body: {
          ...body,
          contact: f.contactName.trim()
            ? { name: f.contactName, role: f.contactRole, email: f.contactEmail, phone: f.contactPhone }
            : null,
        },
      });
    },
    onSuccess: async (d) => {
      qc.setQueryData(detailKey(d.id), d);
      await qc.invalidateQueries({ queryKey: listKey });
      toast(c ? "Klant opgeslagen" : "Klant toegevoegd");
      onClose();
      onSaved?.(d);
    },
    onError: (e) => toast(errMsg(e)),
  });

  return (
    <Modal
      title={c ? "Klant bewerken" : "Nieuwe klant"}
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={save.isPending || !f.name.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Bezig met opslaan…" : c ? "Opslaan" : "Toevoegen"}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Bedrijfsnaam *</span>
        <input value={f.name} onChange={set("name")} autoFocus />
      </label>
      <div className="grid2">
        <label className="field">
          <span>Soort</span>
          <input value={f.type} onChange={set("type")} list="sales-types" placeholder="bijv. Technisch beheerder" />
          <datalist id="sales-types">
            {SALES_CUSTOMER_TYPES.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </label>
        <label className="field">
          <span>Fase</span>
          <select value={f.stage} onChange={set("stage")}>
            {SALES_STAGES.map((s) => (
              <option key={s} value={s}>
                {SALES_STAGE_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {f.stage === "lost" && (
        <label className="field">
          <span>Reden verloren</span>
          <input value={f.lostReason} onChange={set("lostReason")} placeholder="bijv. kiest voor bestaand pakket" />
        </label>
      )}
      <div className="grid2">
        <label className="field">
          <span>Plaats</span>
          <input value={f.city} onChange={set("city")} />
        </label>
        <label className="field">
          <span>KvK-nummer</span>
          <input value={f.kvk} onChange={set("kvk")} inputMode="numeric" />
        </label>
      </div>
      <div className="grid2">
        <label className="field">
          <span>Gebouwen (ongeveer)</span>
          <input value={f.buildings} onChange={set("buildings")} inputMode="numeric" />
        </label>
        <label className="field">
          <span>Klanten of opdrachtgevers</span>
          <input value={f.clients} onChange={set("clients")} inputMode="numeric" />
        </label>
      </div>
      <div className="grid2">
        <label className="field">
          <span>Website</span>
          <input value={f.website} onChange={set("website")} />
        </label>
        <label className="field">
          <span>Herkomst</span>
          <input value={f.source} onChange={set("source")} placeholder="LinkedIn, beurs, doorverwezen…" />
        </label>
      </div>
      <label className="field">
        <span>Organisatie in Klantpunt</span>
        <select value={f.orgId} onChange={set("orgId")}>
          <option value="">Nog geen</option>
          {(orgs.data ?? []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </label>
      {!c && (
        <>
          <p className="label pf-sub">Contactpersoon</p>
          <div className="grid2">
            <label className="field">
              <span>Naam</span>
              <input value={f.contactName} onChange={set("contactName")} />
            </label>
            <label className="field">
              <span>Functie</span>
              <input value={f.contactRole} onChange={set("contactRole")} />
            </label>
          </div>
          <div className="grid2">
            <label className="field">
              <span>E-mail</span>
              <input type="email" value={f.contactEmail} onChange={set("contactEmail")} />
            </label>
            <label className="field">
              <span>Telefoon</span>
              <input type="tel" value={f.contactPhone} onChange={set("contactPhone")} />
            </label>
          </div>
        </>
      )}
      <label className="field">
        <span>Notities</span>
        <textarea rows={3} value={f.notes} onChange={set("notes")} />
      </label>
    </Modal>
  );
}

// ---------------------------------------------------------------- detail

export function SalesCustomerPage() {
  return (
    <PlatformGuard>
      <SalesCustomer />
    </PlatformGuard>
  );
}

type Tab = "overzicht" | "personen" | "logboek";
type Dialog =
  | { type: "edit" }
  | { type: "step" }
  | { type: "contact"; contact?: SalesContactDto }
  | { type: "delete" }
  | { type: "quote" };

function SalesCustomer() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tab = (
    ["overzicht", "personen", "logboek"].includes(params.get("tab") ?? "") ? params.get("tab") : "overzicht"
  ) as Tab;
  const onTab = (t: Tab) => setParams(t === "overzicht" ? {} : { tab: t }, { replace: true });
  const q = useQuery({
    queryKey: detailKey(id),
    queryFn: () => api<SalesCustomerDetail>(`/platform/sales/customers/${id}`),
  });
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const today = todayIso();

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<SalesCustomerDetail>(`/platform/sales/customers/${id}`, { method: "PATCH", body }),
    onSuccess: async (d) => {
      qc.setQueryData(detailKey(id), d);
      await qc.invalidateQueries({ queryKey: listKey });
    },
    onError: (e) => toast(errMsg(e)),
  });

  if (q.isPending || q.isError)
    return (
      <div className="center" aria-busy={!q.isError}>
        {q.isError ? <p className="muted">{errMsg(q.error)}</p> : <Loading />}
      </div>
    );
  const c = q.data;

  const stepDone = () => {
    patch.mutate(
      { nextStepOn: null, nextStepText: "" },
      {
        onSuccess: () => {
          if (c.nextStepText)
            void api(`/platform/sales/customers/${id}/log`, {
              method: "POST",
              body: { kind: "note", text: `Gedaan: ${c.nextStepText}` },
            }).then((d) => qc.setQueryData(detailKey(id), d));
          toast("Volgende stap afgevinkt");
        },
      },
    );
  };

  return (
    <div className="page platform co-page sales-page">
      <PageHeader
        label={<Link to="/platform/klanten">Klanten</Link>}
        title={c.name}
        action={
          <button type="button" className="btn primary" onClick={() => setDialog({ type: "step" })}>
            Volgende stap
          </button>
        }
        menu={
          <>
            <button type="button" className="menuitem" onClick={() => setDialog({ type: "edit" })}>
              <IconPencil size={18} aria-hidden="true" /> Bewerken
            </button>
            <button type="button" className="menuitem" onClick={() => setDialog({ type: "contact" })}>
              <IconPlus size={18} aria-hidden="true" /> Contactpersoon
            </button>
            <button type="button" className="menuitem" onClick={() => setDialog({ type: "quote" })}>
              <IconPlus size={18} aria-hidden="true" /> Offerte maken
            </button>
            <button type="button" className="menuitem danger" onClick={() => setDialog({ type: "delete" })}>
              <IconTrash size={18} aria-hidden="true" /> Verwijderen
            </button>
          </>
        }
        tabs={
          <div className="tabs" role="tablist" aria-label="Onderdelen">
            {(
              [
                ["overzicht", "Overzicht", null],
                ["personen", "Contactpersonen", c.contacts.length],
                ["logboek", "Logboek", c.log.length],
              ] as const
            ).map(([k, label, n]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => onTab(k)}>
                {label}
                {n ? <span className="count num">{n}</span> : null}
              </button>
            ))}
          </div>
        }
      />
      <div className="co-sub">
        <label className="sales-stage">
          <span className="sr-only">Fase</span>
          <select value={c.stage} onChange={(e) => patch.mutate({ stage: e.target.value })} aria-label="Fase">
            {SALES_STAGES.map((s) => (
              <option key={s} value={s}>
                {SALES_STAGE_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <Meta className="muted small" items={[c.type, c.city]} />
        {c.orgId && (
          <Link to={`/platform/${c.orgId}`} className="btn small ghost">
            Organisatie {c.orgName}
          </Link>
        )}
      </div>

      {tab === "overzicht" && (
        <div className="co-grid">
          <div className="co-col">
            <section className="panel co-panel" aria-label="Volgende stap">
              <div className="co-head">
                <h2 className="label">Volgende stap</h2>
                {(c.nextStepOn || c.nextStepText) && (
                  <span className="btnrow">
                    <button type="button" className="btn small" onClick={stepDone} disabled={patch.isPending}>
                      Gedaan
                    </button>
                    <button type="button" className="btn small ghost" onClick={() => setDialog({ type: "step" })}>
                      Wijzigen
                    </button>
                  </span>
                )}
              </div>
              {c.nextStepOn || c.nextStepText ? (
                <p className="sales-step">
                  <NextStep c={c} today={today} />
                  {salesStepLate(c.nextStepOn, today) && <span className="chip tone tone-danger">Te laat</span>}
                </p>
              ) : (
                <p className="muted small">
                  Geen volgende stap.{" "}
                  <button type="button" className="linkbtn inline" onClick={() => setDialog({ type: "step" })}>
                    Plan er een
                  </button>
                </p>
              )}
            </section>
            <section className="panel co-panel" aria-label="Logboek">
              <div className="co-head">
                <h2 className="label">Logboek</h2>
                {c.log.length > 5 && (
                  <button type="button" className="btn small ghost" onClick={() => onTab("logboek")}>
                    Alles
                  </button>
                )}
              </div>
              <LogForm id={id} />
              <LogList items={c.log.slice(0, 5)} id={id} />
            </section>
          </div>
          <div className="co-col">
            <CustomerQuotes customerId={c.id} onNew={() => setDialog({ type: "quote" })} />
            <section className="panel co-panel" aria-label="Gegevens">
              <div className="co-head">
                <h2 className="label">Gegevens</h2>
                <button type="button" className="btn small ghost" onClick={() => setDialog({ type: "edit" })}>
                  Bewerken
                </button>
              </div>
              <dl className="kv">
                {c.type && (
                  <>
                    <dt>Soort</dt>
                    <dd>{c.type}</dd>
                  </>
                )}
                {c.city && (
                  <>
                    <dt>Plaats</dt>
                    <dd>{c.city}</dd>
                  </>
                )}
                <dt>Omvang</dt>
                <dd>
                  <Meta
                    items={[
                      c.buildings ? `${c.buildings} ${c.buildings === 1 ? "gebouw" : "gebouwen"}` : "",
                      c.clients ? `${c.clients} ${c.clients === 1 ? "opdrachtgever" : "opdrachtgevers"}` : "",
                    ]}
                  />
                  {!c.buildings && !c.clients && <span className="muted">Onbekend</span>}
                </dd>
                {c.kvk && (
                  <>
                    <dt>KvK</dt>
                    <dd className="num">{c.kvk}</dd>
                  </>
                )}
                {c.website && (
                  <>
                    <dt>Website</dt>
                    <dd className="dir-wrap">{c.website}</dd>
                  </>
                )}
                {c.source && (
                  <>
                    <dt>Herkomst</dt>
                    <dd>{c.source}</dd>
                  </>
                )}
                <dt>In Klantpunt</dt>
                <dd>{c.orgName ?? <span className="muted">Nog geen organisatie</span>}</dd>
                {c.stage === "lost" && c.lostReason && (
                  <>
                    <dt>Reden verloren</dt>
                    <dd>{c.lostReason}</dd>
                  </>
                )}
              </dl>
              {c.notes && <p className="small dir-notes">{c.notes}</p>}
            </section>
            <section className="panel co-panel" aria-label="Contactpersonen">
              <div className="co-head">
                <h2 className="label">Contactpersonen</h2>
                <button type="button" className="btn small ghost" onClick={() => setDialog({ type: "contact" })}>
                  <IconPlus size={16} aria-hidden="true" /> Persoon
                </button>
              </div>
              <ContactList contacts={c.contacts} onEdit={(p) => setDialog({ type: "contact", contact: p })} compact />
            </section>
          </div>
        </div>
      )}

      {tab === "personen" && (
        <section className="panel co-panel" aria-label="Contactpersonen">
          <div className="co-head">
            <h2 className="label">Contactpersonen</h2>
            <button type="button" className="btn small" onClick={() => setDialog({ type: "contact" })}>
              <IconPlus size={16} aria-hidden="true" /> Contactpersoon
            </button>
          </div>
          <ContactList contacts={c.contacts} onEdit={(p) => setDialog({ type: "contact", contact: p })} />
        </section>
      )}

      {tab === "logboek" && (
        <section className="panel co-panel" aria-label="Logboek">
          <LogForm id={id} />
          <LogList items={c.log} id={id} />
        </section>
      )}

      {dialog?.type === "edit" && <CustomerDialog customer={c} onClose={() => setDialog(null)} />}
      {dialog?.type === "step" && <StepDialog c={c} onClose={() => setDialog(null)} />}
      {dialog?.type === "quote" && <NewQuoteDialog customerId={c.id} onClose={() => setDialog(null)} />}
      {dialog?.type === "contact" && (
        <ContactDialog customerId={c.id} contact={dialog.contact} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "delete" && (
        <Modal
          title="Klant verwijderen"
          onClose={() => setDialog(null)}
          footer={
            <>
              <span className="grow" />
              <button type="button" className="btn ghost" onClick={() => setDialog(null)}>
                Annuleren
              </button>
              <button
                type="button"
                className="btn danger"
                onClick={() =>
                  void api(`/platform/sales/customers/${id}`, { method: "DELETE" })
                    .then(async () => {
                      await qc.invalidateQueries({ queryKey: listKey });
                      toast("Klant verwijderd");
                      void navigate("/platform/klanten");
                    })
                    .catch((e: unknown) => toast(errMsg(e)))
                }
              >
                Verwijderen
              </button>
            </>
          }
        >
          <p>
            {c.name} met contactpersonen en logboek verwijderen? Dit kan niet ongedaan worden gemaakt. Wil je de klant
            alleen uit de lijst hebben, zet de fase dan op <b>Verloren</b>.
          </p>
        </Modal>
      )}
    </div>
  );
}

/** Offertes van deze klant, nieuwste eerst. */
function CustomerQuotes({ customerId, onNew }: { customerId: string; onNew: () => void }) {
  const q = useQuery({
    queryKey: [...quotesKey, customerId],
    queryFn: () => api<SalesQuoteRow[]>(`/platform/sales/quotes?customerId=${customerId}`),
  });
  const today = todayIso();
  return (
    <section className="panel co-panel" aria-label="Offertes">
      <div className="co-head">
        <h2 className="label">Offertes</h2>
        <button type="button" className="btn small ghost" onClick={onNew}>
          <IconPlus size={16} aria-hidden="true" /> Offerte
        </button>
      </div>
      {q.data?.length ? (
        <ul className="co-list">
          {q.data.map((r) => (
            <li key={r.id} className="co-row">
              <span className="grow">
                <Link to={`/platform/offertes/${r.id}`} className="co-rowtitle">
                  {r.number}
                </Link>
                <span className="small muted co-block">
                  {formatEuro(r.monthly)} per maand · {r.title}
                </span>
              </span>
              <QuoteStatusChip status={quoteDisplayStatus(r.status, r.validUntil, today)} />
            </li>
          ))}
        </ul>
      ) : q.isPending ? (
        <Loading small />
      ) : (
        <p className="muted small">Nog geen offerte.</p>
      )}
    </section>
  );
}

function ContactList({
  contacts,
  onEdit,
  compact = false,
}: {
  contacts: SalesContactDto[];
  onEdit: (c: SalesContactDto) => void;
  compact?: boolean;
}) {
  if (!contacts.length) return <p className="muted small">Nog geen contactpersonen.</p>;
  return (
    <ul className="co-list">
      {contacts.map((p) => (
        <li key={p.id} className="co-row">
          <span className="co-av" aria-hidden="true">
            {initials(p.name)}
          </span>
          <span className="grow">
            <span className="co-rowtitle">{p.name}</span>
            {p.isMain && <span className="chip tone tone-accent pf-chip">Hoofdcontact</span>}
            <Meta
              className="small muted co-block"
              items={[p.role, compact ? null : p.email, compact ? null : p.phone]}
            />
            {compact && (p.email || p.phone) && (
              <span className="small co-block dir-wrap">
                <Meta
                  items={[
                    p.email && <a href={`mailto:${p.email}`}>{p.email}</a>,
                    p.phone && <a href={`tel:${p.phone.replace(/[^\d+]/g, "")}`}>{p.phone}</a>,
                  ]}
                />
              </span>
            )}
          </span>
          <button
            type="button"
            className="btn small ghost"
            aria-label={`${p.name} bewerken`}
            title="Bewerken"
            onClick={() => onEdit(p)}
          >
            <IconPencil size={16} aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function LogForm({ id }: { id: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<SalesLogKind>("note");
  const [text, setText] = useState("");
  const add = useMutation({
    mutationFn: () =>
      api<SalesCustomerDetail>(`/platform/sales/customers/${id}/log`, { method: "POST", body: { kind, text } }),
    onSuccess: async (d) => {
      qc.setQueryData(detailKey(id), d);
      await qc.invalidateQueries({ queryKey: listKey });
      setText("");
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <form
      className="sales-logform"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) add.mutate();
      }}
    >
      <select value={kind} onChange={(e) => setKind(e.target.value as SalesLogKind)} aria-label="Soort">
        {SALES_LOG_INPUT_KINDS.map((k) => (
          <option key={k} value={k}>
            {SALES_LOG_KIND_LABELS[k]}
          </option>
        ))}
      </select>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Notitie, gesprek of afspraak…"
        aria-label="Tekst voor het logboek"
      />
      <button type="submit" className="btn" disabled={add.isPending || !text.trim()}>
        Toevoegen
      </button>
    </form>
  );
}

function LogList({ items, id }: { items: SalesCustomerDetail["log"]; id: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  if (!items.length) return <p className="muted small">Nog niets vastgelegd.</p>;
  const remove = (logId: string) =>
    api(`/platform/sales/log/${logId}`, { method: "DELETE" })
      .then(() => qc.invalidateQueries({ queryKey: detailKey(id) }))
      .catch((e: unknown) => toast(errMsg(e)));
  return (
    <ul className="co-list">
      {items.map((l) => (
        <li key={l.id} className="co-row">
          <span className="grow">
            <span className="sales-logtext">{l.text}</span>
            <Meta
              className="small muted co-block"
              items={[SALES_LOG_KIND_LABELS[l.kind], fmtWhen(l.createdAt), l.byName]}
            />
          </span>
          {l.kind !== "stage" && (
            <button
              type="button"
              className="btn small ghost"
              aria-label="Regel verwijderen"
              title="Verwijderen"
              onClick={() => void remove(l.id)}
            >
              <IconTrash size={16} aria-hidden="true" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function StepDialog({ c, onClose }: { c: SalesCustomerDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [on, setOn] = useState(c.nextStepOn ?? "");
  const [text, setText] = useState(c.nextStepText);
  const save = useMutation({
    mutationFn: () =>
      api<SalesCustomerDetail>(`/platform/sales/customers/${c.id}`, {
        method: "PATCH",
        body: { nextStepOn: on || null, nextStepText: text },
      }),
    onSuccess: async (d) => {
      qc.setQueryData(detailKey(c.id), d);
      await qc.invalidateQueries({ queryKey: listKey });
      toast("Volgende stap opgeslagen");
      onClose();
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <Modal
      title="Volgende stap"
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" disabled={save.isPending} onClick={() => save.mutate()}>
            Opslaan
          </button>
        </>
      }
    >
      <label className="field">
        <span>Wat</span>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="bijv. Bellen over de offerte"
          autoFocus
        />
      </label>
      <label className="field">
        <span>Wanneer</span>
        <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
      </label>
    </Modal>
  );
}

function ContactDialog({
  customerId,
  contact: p,
  onClose,
}: {
  customerId: string;
  contact?: SalesContactDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({
    name: p?.name ?? "",
    role: p?.role ?? "",
    email: p?.email ?? "",
    phone: p?.phone ?? "",
    isMain: p?.isMain ?? false,
  });
  const done = async (msg: string) => {
    await qc.invalidateQueries({ queryKey: detailKey(customerId) });
    await qc.invalidateQueries({ queryKey: listKey });
    toast(msg);
    onClose();
  };
  const save = useMutation({
    mutationFn: () =>
      p
        ? api(`/platform/sales/contacts/${p.id}`, { method: "PATCH", body: f })
        : api(`/platform/sales/customers/${customerId}/contacts`, {
            method: "POST",
            body: f.isMain ? f : { ...f, isMain: undefined },
          }),
    onSuccess: () => done(p ? "Contactpersoon opgeslagen" : "Contactpersoon toegevoegd"),
    onError: (e) => toast(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: () => api(`/platform/sales/contacts/${p!.id}`, { method: "DELETE" }),
    onSuccess: () => done("Contactpersoon verwijderd"),
    onError: (e) => toast(errMsg(e)),
  });
  const set = (k: "name" | "role" | "email" | "phone") => (e: { target: { value: string } }) =>
    setF((v) => ({ ...v, [k]: e.target.value }));
  return (
    <Modal
      title={p ? "Contactpersoon bewerken" : "Nieuwe contactpersoon"}
      onClose={onClose}
      busy={save.isPending || remove.isPending}
      footer={
        <>
          {p && (
            <button
              type="button"
              className="btn ghost danger"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              Verwijderen
            </button>
          )}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={save.isPending || !f.name.trim()}
            onClick={() => save.mutate()}
          >
            {p ? "Opslaan" : "Toevoegen"}
          </button>
        </>
      }
    >
      <div className="grid2">
        <label className="field">
          <span>Naam *</span>
          <input value={f.name} onChange={set("name")} autoFocus />
        </label>
        <label className="field">
          <span>Functie</span>
          <input value={f.role} onChange={set("role")} />
        </label>
      </div>
      <div className="grid2">
        <label className="field">
          <span>E-mail</span>
          <input type="email" value={f.email} onChange={set("email")} />
        </label>
        <label className="field">
          <span>Telefoon</span>
          <input type="tel" value={f.phone} onChange={set("phone")} />
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={f.isMain} onChange={(e) => setF((v) => ({ ...v, isMain: e.target.checked }))} />
        Hoofdcontact (krijgt straks de offerte)
      </label>
    </Modal>
  );
}
